"use client";

import { ArrowRight, ArrowUpRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";

import { cn } from "@/core/utils/utils";
import { CaseStudyData } from "../../data/case-studies/types";

interface CaseStudyNextBloomProps {
  nextCaseStudy: CaseStudyData;
}

export function CaseStudyNextBloom({ nextCaseStudy }: CaseStudyNextBloomProps) {
  return (
    <section className="py-24 md:py-36 bg-black relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        <div className="mb-8 font-mono text-xs text-zinc-500 uppercase tracking-wider">
          Up Next
        </div>

        <Link
          href={`/work/${nextCaseStudy.slug}`}
          className={cn(
            "group block w-full rounded-3xl p-8 sm:p-12 lg:p-16 transition-all duration-500 shadow-2xl relative overflow-hidden",
            nextCaseStudy.themeColor
          )}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            
            {/* Left 7 Columns: Next Project Info */}
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-white/80">
                <span>Next Case Study</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>

              <h3 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
                {nextCaseStudy.name}
              </h3>

              <p className="text-lg sm:text-xl text-white/90 font-light max-w-xl leading-relaxed">
                {nextCaseStudy.heroStatement}
              </p>

              <div className="pt-4">
                <span className="inline-flex items-center gap-2 text-base font-semibold text-white underline underline-offset-8 group-hover:opacity-80 transition-opacity">
                  <span>Explore Architecture</span>
                  <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </span>
              </div>
            </div>

            {/* Right 5 Columns: Screenshot Preview */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-black/30 border border-white/10 group-hover:scale-[1.02] transition-transform duration-500">
                <div className="relative w-full aspect-[16/10]">
                  <Image
                    src={nextCaseStudy.heroImage}
                    alt={nextCaseStudy.name}
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 40vw"
                  />
                </div>
              </div>
            </div>

          </div>
        </Link>

      </div>
    </section>
  );
}
